import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  Table,
  Tbody,
  Tr,
  Td,
  Th,
  Thead,
  TableContainer,
  Text,
  Button,
} from "@chakra-ui/react";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Print_pdf_classDetails = forwardRef((props, ref) => {
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const [classDetails, setClassDetails] = useState({
    visibleStudents: [],
    totalStudents: 0,
    genderCounts: { male: 0, female: 0 },
    timetable: [],
    subjects: [],
    days: [],
    exams: [],
    classTeacherName: "Not Assigned",
    leaderName: "Not Assigned",
  });
  const printRef = useRef();

  useImperativeHandle(ref, () => ({
    handlePrint,
  }));
  const classId = localStorage.getItem("classId");
  const divisionId = localStorage.getItem("divisionId");

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`<div>${content}</div>`);
    iframeDoc.close();

    // Wait for the image to load before printing
    const images = iframeDoc.getElementsByTagName("img");
    const loadPromises = Array.from(images).map(
      (img) =>
        new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve; // Resolve on error to prevent blocking
        })
    );

    Promise.all(loadPromises).then(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      document.body.removeChild(iframe);
    });
  };

  useEffect(() => {
    if (classId) {
      setLoading(true);

      // Fetch students, timetable, exams, and class teacher/leader in parallel
      Promise.all([
        axios.get(
          `${BASE_URL}/admin/class/list_assigned_students.php?class_details_id=${classId}`
        ),
        axios.post(
          `${BASE_URL}/admin/timetable/list_all_time_table.php`,
          { class_id: classId, division_id: divisionId }
        ),
        axios.get(
          `${BASE_URL}/admin/exam/list_class_exam.php?class_id=${classId}`
        ),
        axios.get(
          `${BASE_URL}/admin/class/list_single_class.php?class_details_id=${classId}`
        ),
      ])
        .then(
          ([
            studentsResponse,
            timetableResponse,
            examsResponse,
            classDetailsResponse,
          ]) => {
            const {
              data: studentData,
              student_count,
              gender,
            } = studentsResponse.data;
            const maleCount =
              gender.find((g) => g.gender === "Male")?.count || 0;
            const femaleCount =
              gender.find((g) => g.gender === "Female")?.count || 0;

            const timetableData = timetableResponse.data.data.map((item) => ({
              subject: item.subject,
              staffName: item.name,
              startTime: item.start_time,
              endTime: item.end_time,
              day: item.day,
              imageUrl: item.image_url,
            }));

            const uniqueSubjects = timetableData
              .map((item) => ({
                subject: item.subject,
                staffName: item.staffName,
              }))
              .filter(
                (value, index, self) =>
                  index ===
                  self.findIndex(
                    (t) =>
                      t.subject === value.subject &&
                      t.staffName === value.staffName
                  )
              );

            const examsData = examsResponse.data.success
              ? examsResponse.data.data
              : [];

            const classTeacherName =
              classDetailsResponse.data.data.ClassTeacher || "Not Assigned";
            const leaderName =
              classDetailsResponse.data.data.ClassLeader || "Not Assigned";

            setClassDetails({
              visibleStudents: studentData || [],
              totalStudents: student_count,
              genderCounts: { male: maleCount, female: femaleCount },
              timetable: timetableData,
              subjects: uniqueSubjects,
              days: [], // Set to empty or populate if day data is fetched separately
              exams: examsData,
              classTeacherName,
              leaderName,
            });
            setIsDataLoaded(true); // Mark data as loaded
          }
        )
        .catch((error) => console.error("Error fetching data:", error))
        .finally(() => setLoading(false));
    }
  }, [classId, divisionId]);

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  const timeSlots = [
    "8:00 AM - 9:00 AM",
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "12:00 PM - 1:00 PM",
  ];

  // Create timetable grid with periods as columns and days as rows
  const timetableGrid = daysOfWeek.map((day) => {
    return timeSlots.map((timeSlot) => {
      // Find the subject for the given day and time slot
      const subjectEntry = classDetails.timetable.find(
        (entry) =>
          entry.day === day && entry.startTime === timeSlot.split(" - ")[0]
      );
      return subjectEntry
        ? `${subjectEntry.subject} (${subjectEntry.staffName})`
        : ""; // Show subject and staff name
    });
  });

  return (
    <div
      ref={printRef}
      style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "40px",
          borderBottom: "2px solid #000",
          paddingBottom: "20px",
          textAlign: "center",
        }}
      >
        <img
          src="https://www.vimalagiriinternational.com/static/media/logo-removebg-preview%20(1).1b7b766c8c63e51dbc52.png"
          alt="Vimalagiri Logo"
          style={{ width: "180px", height: "auto", marginBottom: "10px" }}
          onError={() => console.error("Error loading logo image")}
        />
        <div style={{ marginLeft: "20px" }}>
          <h1
            style={{
              margin: "0",
              fontSize: "72px",
              fontWeight: "bold",
              lineHeight: "0.7",
              fontFamily: "'Times New Roman', serif",
            }}
          >
            Vimalagiri
          </h1>
          <h2
            style={{
              margin: "0",
              fontSize: "28px",
              fontWeight: "bold",
              marginTop: "10px",
              fontFamily: "'Times New Roman', serif",
            }}
          >
            International School
          </h2>
          <Text style={{ fontSize: "16px", color: "#777", marginTop: "10px" }}>
            Affiliated to CISCE New Delhi Aff.No.KE143
          </Text>
        </div>
      </div>

      {/* Class Details */}
      <div style={{ marginTop: "20px" }}>
        <h2>Class Details</h2>
        <p>
          <strong>Class Teacher:</strong> {classDetails.classTeacherName}
        </p>
        <p>
          <strong>Class Leader:</strong> {classDetails.leaderName}
        </p>
      </div>

      {/* Student List */}
      <div style={{ marginTop: "20px" }}>
        <h2>Student List</h2>
        <TableContainer>
          <Table
            sx={{
              "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
            }}
            style={{
              border: "1px solid #d4c2c2",
              borderCollapse: "collapse",
              width: "100%",
            }}
          >
            <Thead>
              <Tr>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  Adm. No.
                </Th>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  Name
                </Th>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  Number
                </Th>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  Address
                </Th>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  DoB
                </Th>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  Father Name
                </Th>
                <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                  Mother Name
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {classDetails.visibleStudents.map((student, index) => (
                <Tr key={index}>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.admission_number}
                  </Td>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.name}
                  </Td>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.father_number
                      ? student.father_number
                      : student.mother_number}
                  </Td>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.address}
                  </Td>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.date_of_birth}
                  </Td>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.father_name}
                  </Td>
                  <Td style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                    {student.mother_name}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Page Break for Timetable */}
      <div className="page-break"></div>

      {/* Timetable */}
      <div style={{ marginBottom: "40px", pageBreakBefore: "always" }}>
        <h2>Timetable</h2>
        <TableContainer>
          <Table
            sx={{
              "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
            }}
          >
            <Thead>
              <Tr>
                {/* <Th>Day</Th> */}
                {/* Dynamically create columns for each period_id */}
                {classDetails.timetable.map((entry, index) => (
                  <Th key={index}>{entry.period_id}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {/* Loop through days and create a row for each */}
              {daysOfWeek.map((day, rowIndex) => (
                <Tr key={rowIndex}>
                  <Td>{day}</Td>
                  {/* Loop through period_id and find the matching subject for each period */}
                  {classDetails.timetable
                    .filter((entry) => entry.day === day) // Filter based on day
                    .map((entry, colIndex) => (
                      <Td key={colIndex}>
                        {entry.subject} ({entry.staffName})
                      </Td>
                    ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      {/* Page Break for Exam Details */}
      <div className="page-break"></div>

      {/* Exam Details */}

      <div style={{ marginTop: "40px" }}>
        <h2>Exam Details</h2>

        {/* Group exams by category */}
        {Object.entries(
          (Array.isArray(classDetails.exams) ? classDetails.exams : []).reduce(
            (categories, exam) => {
              const category = exam.category || "Uncategorized"; // Use "Uncategorized" if no category exists
              if (!categories[category]) {
                categories[category] = [];
              }
              categories[category].push(exam);
              return categories;
            },
            {}
          )
        ).map(([categoryName, categoryExams]) => (
          <div key={categoryName}>
            <h3>{categoryName}</h3>
            <TableContainer>
              <Table
                sx={{
                  "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
                }}
                style={{
                  border: "1px solid #d4c2c2",
                  borderCollapse: "collapse",
                  width: "100%",
                }}
              >
                <Thead>
                  <Tr>
                    <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                      Serial No.
                    </Th>
                    <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                      Exam Name
                    </Th>
                    <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                      Subject
                    </Th>
                    <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                      Max Marks
                    </Th>
                    <Th style={{ border: "1px solid #d4c2c2", padding: "8px" }}>
                      Pass Marks
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {categoryExams.map((exam, index) => (
                    <Tr key={index}>
                      <Td
                        style={{ border: "1px solid #d4c2c2", padding: "8px" }}
                      >
                        {index + 1}
                      </Td>
                      <Td
                        style={{ border: "1px solid #d4c2c2", padding: "8px" }}
                      >
                        {exam.category}
                      </Td>
                      <Td
                        style={{ border: "1px solid #d4c2c2", padding: "8px" }}
                      >
                        {exam.subject}
                      </Td>
                      <Td
                        style={{ border: "1px solid #d4c2c2", padding: "8px" }}
                      >
                        {exam.max_marks}
                      </Td>
                      <Td
                        style={{ border: "1px solid #d4c2c2", padding: "8px" }}
                      >
                        {exam.pass_marks}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </div>
        ))}
      </div>
    </div>
  );
});

export default Print_pdf_classDetails;
